import { FC, useMemo } from "react";
import ControlledSelect, { ControlledSelectProps } from "../../../ControlledInputs/ControlledSelect.tsx";
import { useWatch } from "react-hook-form";
import useAvailableSlots from "../../../../api/hooks/useAvailableSlots/useAvailableSlots.ts";
import { AvailableSlot } from "../../../../api/hooks/useAvailableSlots/type.ts";
import { addMinutes, format } from "date-fns";
import { useBooking } from "../../../../utils/context/Booking/BookingContext.tsx";
import { useParams } from "react-router-dom";

function filterSlots(availableSlots: AvailableSlot[], hoursToBook: number): AvailableSlot[] {
  const minutesToBook = hoursToBook * 60;
  const expectedIntervalsCount = minutesToBook / 30 - 1;
  const filteredSlots: AvailableSlot[] = [];

  for (let i = 0; i < availableSlots.length; i++) {
    const currentSlot = availableSlots[i];
    const currentSlotDate = new Date(currentSlot.slotDateTime);
    const slotsPickUpUntil = addMinutes(currentSlotDate, minutesToBook);
    const validSlots: AvailableSlot[] = [];

    for (const slot of availableSlots) {
      const slotDate = new Date(slot.slotDateTime);
      if (slotDate < currentSlotDate) {
        continue;
      }

      if (slotDate <= slotsPickUpUntil) {
        validSlots.push(slot);
      } else {
        break;
      }
    }

    if (validSlots.length >= expectedIntervalsCount) {
      filteredSlots.push(currentSlot);
    }
  }

  return filteredSlots;
}

const SlotSelect: FC<Omit<ControlledSelectProps, "options" | "disabled">> = (props) => {
  const hall = useWatch({
    control: props.control,
    name: "hall",
  });

  const date = useWatch({
    control: props.control,
    name: "date",
  });

  const hoursNumber = useWatch({
    control: props.control,
    name: "hoursNumber",
  });

  const { slots, isFetching } = useAvailableSlots({
    date: date ? format(new Date(date), "yyyy-MM-dd'T'00:00:00.000'Z'") : null,
    hallId: hall,
  });

  const { booking } = useBooking();
  const { id } = useParams()

  const filteredSlots = useMemo(() => filterSlots(slots, hoursNumber).map(s => ({
    label: s.slotValue,
    value: s.slotDateTime,
  })), [hoursNumber, slots]);

  const isSlotAbsent = id !== 'new' && !filteredSlots.some(s => booking?.slotDate && s.label === format(new Date(booking?.slotDate), "HH:mm" ));

  return (
    <ControlledSelect key={hoursNumber} disabled={isFetching || !hall || !date || !hoursNumber} options={isSlotAbsent ? [{
      label: format(new Date(booking?.slotDate!), "HH:mm" ),
      value: booking?.slotDate!
    },...filteredSlots] : filteredSlots} {...props} />
  );
};

export default SlotSelect;
